/**
 * Event catalog (ARCHITECTURE.md §7.1). Every state-changing service method writes an activity_event
 * and an outbox row with one of these types in the same transaction.
 */
export const EventType = {
  PROJECT_CREATED: 'project.created',
  PROJECT_UPDATED: 'project.updated',
  PROJECT_MEMBER_CHANGED: 'project.member_changed',

  ITEM_CREATED: 'item.created',
  ITEM_UPDATED: 'item.updated',
  ITEM_DELETED: 'item.deleted',
  ITEM_RESTORED: 'item.restored',
  /** Retention redacted a soft-deleted item past its restore window (FR-ADM-3). */
  ITEM_PURGED: 'item.purged',
  ITEM_STATUS_CHANGED: 'item.status_changed',
  ITEM_ASSIGNED: 'item.assigned',
  ITEM_ROLE_CHANGED: 'item.role_changed',
  ITEM_RELEASE_CHANGED: 'item.release_changed',
  ITEM_COMPONENT_CHANGED: 'item.component_changed',
  ITEM_CONVERTED_FROM_QUICK_TASK: 'item.converted_from_quick_task',
  ITEM_SPRINT_CHANGED: 'item.sprint_changed',
  ITEM_RANKED: 'item.ranked',
  ITEM_LINKED: 'item.linked',
  ITEM_UNLINKED: 'item.unlinked',
  ITEM_WATCHER_CHANGED: 'item.watcher_changed',
  HANDOFF_CREATED: 'handoff.created',
  ATTACHMENT_ADDED: 'attachment.added',

  COMMENT_CREATED: 'comment.created',
  COMMENT_UPDATED: 'comment.updated',
  COMMENT_DELETED: 'comment.deleted',
  MENTION_CREATED: 'mention.created',

  SPRINT_CREATED: 'sprint.created',
  SPRINT_UPDATED: 'sprint.updated',
  SPRINT_STARTED: 'sprint.started',
  SPRINT_COMPLETED: 'sprint.completed',
  /** Mid-sprint risk check found the sprint off track (FR-AI-9). */
  SPRINT_AT_RISK: 'sprint.at_risk',
  /** Critical bug raised for a premium/strategic customer (FR-INT-6). */
  ITEM_ESCALATED: 'item.escalated',
  CUSTOMER_CHANGED: 'customer.changed',
  BOARD_UPDATED: 'board.updated',

  RELEASE_CREATED: 'release.created',
  RELEASE_UPDATED: 'release.updated',
  BUILD_REGISTERED: 'build.registered',
  BUILD_READY_FOR_QA: 'build.ready_for_qa',
  BUILD_STATE_CHANGED: 'build.state_changed',
  BUILD_PROMOTED: 'build.promoted',
  BUILD_CONTENTS_CHANGED: 'build.contents_changed',
  BUILD_KEY_REVEALED: 'build.key_revealed',

  WORKLOG_CREATED: 'worklog.created',
  WORKLOG_UPDATED: 'worklog.updated',
  WORKLOG_DELETED: 'worklog.deleted',
  TIMER_STARTED: 'timer.started',
  TIMER_STOPPED: 'timer.stopped',

  NOTE_CREATED: 'note.created',
  NOTE_UPDATED: 'note.updated',
  NOTE_DELETED: 'note.deleted',
  NOTE_ASSIGNED: 'note.assigned',
  NOTE_ASSIGNMENT_UPDATED: 'note.assignment_updated',
  NOTE_ITEM_CREATED: 'note.item_created',

  DOCUMENT_CREATED: 'document.created',
  DOCUMENT_UPDATED: 'document.updated',
  DOCUMENT_DELETED: 'document.deleted',
  DOCUMENT_VERSION_ADDED: 'document.version_added',
  DOCUMENT_VERSION_PROCESSED: 'document.version_processed',
  DOCUMENT_LINKED: 'document.linked',
  DOCUMENT_STATUS_CHANGED: 'document.status_changed',
  DOCUMENT_COMMENTED: 'document.commented',
  DOCUMENT_VIEWED: 'document.viewed',
  DOCUMENT_DOWNLOADED: 'document.downloaded',
  DOCUMENT_RESTORED: 'document.restored',
  DOCUMENT_LEGAL_HOLD: 'document.legal_hold',
  DOCUMENT_PURGED: 'document.purged',
  REQUIREMENTS_EXTRACTED: 'requirements.extracted',
  REQUIREMENTS_ITEMS_CREATED: 'requirements.items_created',

  MEDIA_UPLOADED: 'media.uploaded',
  MEDIA_PROCESSED: 'media.processed',
  MEDIA_DELETED: 'media.deleted',
  MEDIA_TRANSCRIBED: 'media.transcribed',
  MEDIA_SUMMARISED: 'media.summarised',
  MEDIA_COMMENTED: 'media.commented',
  MEDIA_TRIMMED: 'media.trimmed',

  USER_UPDATED: 'user.updated',
  TEAM_CHANGED: 'team.changed',
  SETTINGS_UPDATED: 'settings.updated',
  LEAVE_CHANGED: 'leave.changed',

  TIMESHEET_SUBMITTED: 'timesheet.submitted',
  TIMESHEET_DECIDED: 'timesheet.decided',
  TIMESHEET_REOPENED: 'timesheet.reopened',
  TIMESHEET_REMINDER: 'timesheet.reminder',
  TRIAGE_AGEING_ALERT: 'triage.ageing_alert',
  DIGEST_SENT: 'digest.sent',

  APPROVAL_REQUESTED: 'approval.requested',
  APPROVAL_DECIDED: 'approval.decided',
  AUTOMATION_RULE_CHANGED: 'automation.rule_changed',
  AUTOMATION_RULE_FIRED: 'automation.rule_fired',
  RETRO_CHANGED: 'retro.changed',
  CHANNEL_CHANGED: 'channel.changed',
  GIT_COMMIT_LINKED: 'git.commit_linked',
  GIT_PR_UPDATED: 'git.pr_updated',
  GIT_CONNECTION_CHANGED: 'git.connection_changed',
  MESSAGE_POSTED: 'message.posted',
  MESSAGE_UPDATED: 'message.updated',
  MESSAGE_DELETED: 'message.deleted',
  STANDUP_POSTED: 'standup.posted',
  PAGE_CREATED: 'page.created',
  PAGE_UPDATED: 'page.updated',
  PAGE_DELETED: 'page.deleted',
  IMPORT_COMPLETED: 'import.completed',

  AI_PROPOSAL_CONFIRMED: 'ai.proposal_confirmed',
  AI_PROPOSAL_CREATED: 'ai.proposal_created',

  TEST_CASE_CHANGED: 'test_case.changed',
  TEST_SUITE_CHANGED: 'test_suite.changed',
  TEST_PLAN_CHANGED: 'test_plan.changed',
  TEST_RUN_CREATED: 'test_run.created',
  TEST_RESULT_RECORDED: 'test_result.recorded',
  TEST_RUN_COMPLETED: 'test_run.completed',
  RESULT_DEFECT_LINKED: 'result_defect.linked',
  TEST_SESSION_STOPPED: 'test_session.stopped',
} as const;
export type EventType = (typeof EventType)[keyof typeof EventType];

/** Socket.IO room helpers (ARCHITECTURE.md §9). */
export const Rooms = {
  user: (id: string) => `user:${id}`,
  board: (id: string) => `board:${id}`,
  project: (id: string) => `project:${id}`,
  item: (key: string) => `item:${key}`,
  build: (id: string) => `build:${id}`,
  run: (id: string) => `run:${id}`,
  channel: (id: string) => `channel:${id}`,
} as const;

/** Realtime message names pushed to clients. */
export const RealtimeEvent = {
  ITEM_CHANGED: 'item.changed',
  ITEM_DELETED: 'item.deleted',
  COMMENT_ADDED: 'comment.added',
  NOTIFICATION: 'notification',
  TIMER: 'timer',
  NOTE_CHANGED: 'note.changed',
  BUILD_CHANGED: 'build.changed',
  PROCESSING: 'processing',
  RUN_CHANGED: 'run.changed',
  RETRO_CHANGED: 'retro.changed',
  MESSAGE: 'message',
  CHANNEL_READ: 'channel.read',
  TYPING: 'typing',
  PRESENCE: 'presence',
} as const;
